import axios from "axios";
import config from "./config";
import isMobileHelper from "./utils/isMobile";

// Create axios instance
const api = axios.create({
  baseURL: config.API_BASE_URL,
  timeout: 1800000, // 30 minutes
  withCredentials: false,
  headers: {
    "Content-Type": "application/json",
  },
});

// 🔄 Used to coordinate token refresh requests
let isRefreshing = false;
let refreshSubscribers = [];

// Notify queued requests when refresh completes
function onRefreshed(newToken) {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
}

// Queue requests that occur while token refresh is ongoing
function addRefreshSubscriber(callback) {
  refreshSubscribers.push(callback);
}

// Request interceptor (optional token attach by AuthContext)
api.interceptors.request.use(
  (config) => {
    // Allow callers to explicitly force credentials for a single request by
    // passing { forceWithCredentials: true } in the axios request config.
    // Otherwise, auto-enable credentials for mobile user agents so mobile
    // login flows that rely on cookies work without changing all call sites.
    if (typeof config.forceWithCredentials !== "undefined") {
      // explicit per-request override
      config.withCredentials = !!config.forceWithCredentials;
    } else if (isMobileHelper()) {
      // enable credentials automatically on mobile (to match mobile login behavior)
      config.withCredentials = true;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for handling errors & retries
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        // Wait for ongoing refresh to complete
        return new Promise((resolve) => {
          addRefreshSubscriber((token) => {
            if (token) {
              originalRequest.headers["Authorization"] = `Bearer ${token}`;
            }
            resolve(api(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        // 🔔 Trigger refresh through AuthContext
        const event = new CustomEvent("tokenRefreshNeeded");
        window.dispatchEvent(event);

        // Wait a short period for AuthContext to refresh
        const newToken = await new Promise((resolve) => {
          const listener = (e) => {
            resolve(e.detail?.token || null);
            window.removeEventListener("tokenRefreshed", listener);
          };
          window.addEventListener("tokenRefreshed", listener);

          // Fallback timeout if no response
          setTimeout(() => {
            resolve(null);
            window.removeEventListener("tokenRefreshed", listener);
          }, 3000);
        });

        isRefreshing = false;
        onRefreshed(newToken);

        if (newToken) {
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          return api(originalRequest);
        } else {
          console.warn("[API] Token refresh failed, redirecting to login");
          window.location.href = "/login";
        }
      } catch (refreshError) {
        isRefreshing = false;
        console.error("[API] Token refresh error:", refreshError);
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    // Handle network errors
    if (!error.response) {
      console.error("[API] Network error:", error.message);
      throw new Error("Network error — please check your connection");
    }

    // Generic error propagation
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      "An unexpected error occurred";

    const enhancedError = new Error(errorMessage);
    enhancedError.status = error.response?.status;
    enhancedError.data = error.response?.data;
    enhancedError.originalError = error;

    return Promise.reject(enhancedError);
  }
);

// 🌱 Optional: debug logging in development
if (process.env.NODE_ENV === "development") {
  api.interceptors.request.use((config) => {
    console.log(`🚀 [API] ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  });

  api.interceptors.response.use(
    (response) => {
      console.log(`✅ [API] ${response.status} ${response.config.url}`);
      return response;
    },
    (error) => {
      console.log(
        `❌ [API] ${error.response?.status || "ERR"} ${error.config?.url}`
      );
      return Promise.reject(error);
    }
  );
}

export default api;
